import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import DropdownField from "../../../../../../components/input-fields/DropdownField";
import useIsMobile from "../../../../../../hooks/useIsMobile";
import { getFeatureFlag } from "../../../../../../services-logic/base-logic";
import { postOpenModal } from "../../../../../../utils/utilPostMessage";
import CustomDurationModal from "./CustomDurationModal";

export const durationToText = (duration) => {
  if (duration === null || duration === undefined) {
    return "";
  }

  if (duration === 60) {
    return "1 hour";
  } else if (duration > 60) {
    if (duration % 30 === 0) {
      return `${duration / 60} hours`;
    } else {
      const hours = Math.floor(duration / 60);
      if (hours === 1) {
        return `${hours} hour and ${duration % 60} minutes`;
      }

      return `${hours} hours and ${duration % 60} minutes`;
    }
  }
  return `${duration} minutes`;
};

const DurationDropdown = ({ contactIndex, duration, onChange, errorMessage }) => {
  const [customOption, setCustomOption] = useState(null);
  const { externalOverlay } = useSelector((state) => state.info);
  const [isCustomDurationModalOpen, setCustomDurationModalOpen] = useState(false);
  const [modalStyles, setModalStyles] = useState("");
  const [durationOptions, setDurationOptions] = useState([]);

  const appointmentDurations = getFeatureFlag(
    "Account.CalendarManagement.AppointmentDurations",
    null,
    [15, 30, 45, 60, 90, 120, 180]
  );

  const isMobile = useIsMobile();

  const dropdownRef = useRef(null);

  useEffect(() => {
    if (duration && duration !== "custom" && !appointmentDurations.includes(duration)) {
      appointmentDurations.push(duration);
    }

    const sorted = appointmentDurations.filter(d => d !== "custom").sort((a, b) => a - b);

    // Avoid adding "Custom" option more than once
    const options = sorted.filter(d => d !== "custom").map((duration) => {
      return { value: duration, option: durationToText(duration) };
    });

    setDurationOptions(options);
  }, [duration, customOption]);

  const onCloseModal = () => {
    onChange(duration);
    setCustomOption(null);
    setCustomDurationModalOpen(false);
  };

  const addCustomDuration = (value) => {
    if (value && !durationOptions.some((option) => option.value === value)) {
      const newOption = {
        value,
        option: durationToText(value),
      };

      // Add new option in the correct order by value
      const newOptions = [...durationOptions, newOption].sort((a, b) => a.value - b.value);
      setDurationOptions(newOptions);
    }
    onChange(value);
    setCustomDurationModalOpen(false);
  };

  const showCustomDurationModal = (index) => {
    if (externalOverlay) {
      const message = {
        childrenType: "custom-duration",
        id: "custom-duration",
        minDuration: 5,
        contactIndex: contactIndex,
      };

      postOpenModal(message);
    } else {
      if (dropdownRef && dropdownRef.current && typeof dropdownRef.current.getBoundingClientRect === "function") {
        const accordionRect = dropdownRef.current.getBoundingClientRect();

        setModalStyles({
          position: "absolute",
          top: `${accordionRect.top - 10}px `,
          left: "50%",
          transform: "translateX(-50%)",
        });
      } else {
        setModalStyles({});
      }

      setCustomDurationModalOpen(true);
    }
  };

  useEffect(() => {
    if (externalOverlay) {
      const handleMessage = (event) => {
        const data = event.data;
        if (data.type === "overlay-to-snippet") {
          const extra = data.extra;
          if (extra.action === "overlay-closed") {
            onCloseModal();
            return;
          }

          if (extra.id !== "custom-duration") return;

          if (extra.contactIndex === contactIndex && extra.action === "confirm-duration") {
            addCustomDuration(extra.duration);
          }
        }
      };

      // Add the event listener
      window.addEventListener("message", handleMessage);

      // Clean up the event listener on component unmount or when externalOverlay changes
      return () => {
        window.removeEventListener("message", handleMessage);
      };
    }
  }, [externalOverlay, onChange]);

  const mergedDurations = useMemo(() => {
    const isCustomAllowed = appointmentDurations.includes("custom");
    if (!isCustomAllowed) {
      return durationOptions;
    }

    return [...durationOptions, { value: "custom", option: "Custom..." }];
  }, [durationOptions]);

  return (
    <div>
      <DropdownField
        title={"Duration"}
        defaultOption={{ value: null, option: "Duration" }}
        options={mergedDurations}
        selectedOption={duration}
        onChange={(duration) => {
          if (duration === "custom") {
            showCustomDurationModal();
          } else {
            onChange(duration);
          }
        }}
        openDirection={isMobile ? "up" : "down"}
        errorMessage={errorMessage}
      />
      {!externalOverlay && isCustomDurationModalOpen && (
        <CustomDurationModal
          onClose={onCloseModal}
          onConfirm={(duration) => {
            addCustomDuration(duration);
          }}
          onCancel={onCloseModal}
          modalStyles={modalStyles}
          dataFromSnippet={{ minDuration: 5, contactIndex: contactIndex }}
        />
      )}
    </div>
  );
};

export default DurationDropdown;
